import React, { useEffect, useState } from 'react';
import { Student } from '../types';
import Button from './ui/Button';
import Modal from './ui/Modal';
import ImagePreview from './ui/ImagePreview';
import { useStudent } from '../hooks/student/useStudent';
import { getStatusBadgeColor } from '../utils/student';
import { onStudentsSummary, StudentsSummary } from '../services/summary.service';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
    GraduationCap,
    Search,
    Edit,
    Eye,
    User,
    ChevronLeft,
    ChevronRight,
    Plus,
    BookOpen,
    MapPin,
    Users,
    UserCheck,
    UserX,
    RefreshCw,
    SlidersHorizontal,
    X,
    Calendar,
} from 'lucide-react';
import { getCampuses, getClassesWithCampus, getBadges } from '../utils/management';

interface StudentViewProps {
    students: Student[];
    onEdit: (student: Student) => void;
    onToggleStatus: (studentId: string, newStatus: 'active' | 'inactive', reason?: string) => void;
    onAddStudent?: () => void;
    loading?: boolean;
}

const StatCard: React.FC<{
    label: string;
    value: number | string;
    icon: React.ElementType;
    iconColor: string;
}> = ({ label, value, icon: Icon, iconColor }) => (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${iconColor} shrink-0`}>
            <Icon className="h-5 w-5" />
        </div>
        <div>
            <p className="text-xs text-gray-500 font-medium">{label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        </div>
    </div>
);

const StudentView: React.FC<StudentViewProps> = ({
    students,
    onEdit,
    onToggleStatus,
    onAddStudent,
    loading = false,
}) => {
    const {
        searchTerm, setSearchTerm,
        currentPage,
        itemsPerPage,
        viewingStudent, setViewingStudent,
        showStatusModal, setShowStatusModal,
        selectedStudent, setSelectedStudent,
        statusReason, setStatusReason,
        showImageModal, setShowImageModal,
        selectedImageUrl, selectedImageAlt,
        handlePageChange, handleItemsPerPageChange,
        handleStatusToggle, confirmStatusChange, handleImageClick,
    } = useStudent(students, onToggleStatus);

    const [campuses, setCampuses] = useState<any[]>([]);
    const [dbClasses, setDbClasses] = useState<any[]>([]);
    const [badges, setBadges] = useState<any[]>([]);

    // Custom Professional Filters state
    const [selectedBadge, setSelectedBadge] = useState<string>('all');
    const [selectedClass, setSelectedClass] = useState<string>('all');
    const [filterAdmissionFrom, setFilterAdmissionFrom] = useState<string>('');
    const [filterAdmissionTo, setFilterAdmissionTo] = useState<string>('');
    const [localFilterStatus, setLocalFilterStatus] = useState<string>('all');
    const [filterHasImage, setFilterHasImage] = useState<'all' | 'yes' | 'no'>('all');
    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

    const [contextMenu, setContextMenu] = useState<{
        x: number;
        y: number;
        student: Student;
    } | null>(null);

    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const [campusesData, classesData, badgesData] = await Promise.all([
                    getCampuses(),
                    getClassesWithCampus(),
                    getBadges()
                ]);
                setCampuses(campusesData);
                setDbClasses(classesData);
                setBadges(badgesData);
            } catch (error) {
                console.error('Error loading metadata in StudentView:', error);
            }
        };
        loadMetadata();
    }, []);

    // Filter computation
    const localFilteredStudents = React.useMemo(() => {
        return students.filter(student => {
            // Search term matches
            const matchesSearch =
                student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.academicInfo.currentGrade.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.academicInfo.section.toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch) return false;

            // Status filter
            if (localFilterStatus !== 'all' && student.academicInfo.status !== localFilterStatus) {
                return false;
            }

            // Badge filter
            if (selectedBadge !== 'all') {
                const sBadge = (student.academicInfo as any)?.badge || (student as any).badge;
                const badgeTitle = typeof sBadge === 'string' ? sBadge : (sBadge?.title || sBadge);
                if (!badgeTitle || badgeTitle !== selectedBadge) return false;
            }

            // Class filter
            if (selectedClass !== 'all') {
                const currentClassStr = `${student.academicInfo.currentGrade}|${student.academicInfo.section}`;
                if (currentClassStr !== selectedClass) return false;
            }

            // Has Profile Image filter
            if (filterHasImage !== 'all') {
                const hasImg = !!student.profileImage;
                if (filterHasImage === 'yes' && !hasImg) return false;
                if (filterHasImage === 'no' && hasImg) return false;
            }

            // Admission Date Range filter
            if (student.academicInfo.admissionDate) {
                const admDate = new Date(student.academicInfo.admissionDate);
                admDate.setHours(0, 0, 0, 0);

                if (filterAdmissionFrom) {
                    const fromDate = new Date(filterAdmissionFrom);
                    fromDate.setHours(0, 0, 0, 0);
                    if (admDate < fromDate) return false;
                }

                if (filterAdmissionTo) {
                    const toDate = new Date(filterAdmissionTo);
                    toDate.setHours(0, 0, 0, 0);
                    if (admDate > toDate) return false;
                }
            } else if (filterAdmissionFrom || filterAdmissionTo) {
                return false;
            }

            return true;
        });
    }, [students, searchTerm, localFilterStatus, selectedBadge, selectedClass, filterHasImage, filterAdmissionFrom, filterAdmissionTo]);

    // Redefining standard hooks outputs locally
    const filteredStudents = localFilteredStudents;
    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedStudents = filteredStudents.slice(startIndex, startIndex + itemsPerPage);

    const isAnyFilterActive = 
        selectedBadge !== 'all' || 
        selectedClass !== 'all' || 
        filterAdmissionFrom !== '' || 
        filterAdmissionTo !== '' || 
        localFilterStatus !== 'all' || 
        filterHasImage !== 'all';

    const resetFilters = () => {
        setSelectedBadge('all');
        setSelectedClass('all');
        setFilterAdmissionFrom('');
        setFilterAdmissionTo('');
        setLocalFilterStatus('all');
        setFilterHasImage('all');
    };

    useEffect(() => {
        const handleClose = () => setContextMenu(null);
        window.addEventListener('click', handleClose);
        return () => window.removeEventListener('click', handleClose);
    }, []);

    const getStudentCampusName = (student: Student) => {
        const studentClass = dbClasses.find(c => c.grade === student.academicInfo.currentGrade && c.section === student.academicInfo.section);
        if (!studentClass) return '—';
        const studentCampus = campuses.find(cam => cam.id === studentClass.campusId);
        return studentCampus ? studentCampus.name : '—';
    };

    const getPaginationRange = () => {
        const delta = 1; // Number of pages to show before and after current page
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let l: number | undefined;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        for (const i of range) {
            if (typeof i === 'number') {
                if (l !== undefined) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l > 2) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }
        }

        return rangeWithDots;
    };

    // Live summary from /analytics/studentsSummary
    const [summary, setSummary] = useState<StudentsSummary>({ total: 0, active: 0, inactive: 0, graduated: 0 });
    useEffect(() => {
        const unsub = onStudentsSummary(setSummary);
        return () => unsub();
    }, []);

    /* ── Status pill ─────────────────────────────────────────────── */
    const StatusToggle: React.FC<{ student: Student }> = ({ student }) => {
        const isActive = student.academicInfo.status === 'active';
        return (
            <button
                onClick={() => handleStatusToggle(student)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                    isActive ? 'bg-red-500' : 'bg-gray-300'
                }`}
            >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
        );
    };

    /* ── Avatar ──────────────────────────────────────────────────── */
    const Avatar: React.FC<{ student: Student; size?: 'sm' | 'md' }> = ({ student, size = 'sm' }) => {
        const dim = size === 'sm' ? 'w-8 h-8' : 'w-20 h-20';
        const icon = size === 'sm' ? 'h-4 w-4' : 'h-10 w-10';
        const name = `${student.firstName} ${student.lastName}`;
        const initials = `${student.firstName[0] ?? ''}${student.lastName?.[0] ?? ''}`.toUpperCase();

        return (
            <div
                className={`${dim} rounded-full overflow-hidden shrink-0 cursor-pointer`}
                onClick={() => student.profileImage && handleImageClick(student.profileImage, name)}
            >
                {student.profileImage ? (
                    <img src={student.profileImage} alt={name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full bg-emerald-500 flex items-center justify-center text-white font-bold text-xs">
                        {initials || <GraduationCap className={icon} />}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-5">

            {/* ── Page header ─────────────────────────────────────────── */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Students</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Manage student accounts and profiles for your school.</p>
                </div>
                {onAddStudent && (
                    <button
                        onClick={onAddStudent}
                        className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Create Student
                    </button>
                )}
            </div>

            {/* ── Stat cards ──────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard label="Total Students"    value={summary.total}    icon={Users}      iconColor="bg-blue-50 text-blue-500" />
                <StatCard label="Active Students"   value={summary.active}   icon={UserCheck}  iconColor="bg-emerald-50 text-emerald-600" />
                <StatCard label="Inactive Students" value={summary.inactive} icon={UserX}      iconColor="bg-red-50 text-red-500" />
            </div>

            {/* ── Table card ──────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">

                {/* Table toolbar */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h2 className="text-base font-bold text-gray-900">Student Information</h2>
                    <div className="flex items-center gap-3">
                        {/* Search */}
                        <div className="relative">
                            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Search"
                                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 w-52"
                            />
                        </div>
                        {/* Custom Filter Button */}
                        <div className="relative">
                            <button
                                onClick={() => setShowFilterModal(true)}
                                className={`flex items-center gap-2 text-sm border rounded-lg px-4 py-2 hover:bg-gray-50 transition-all font-semibold ${
                                    isAnyFilterActive 
                                        ? 'border-emerald-500 bg-emerald-50/50 text-emerald-700' 
                                        : 'border-gray-200 text-gray-700 bg-white'
                                }`}
                            >
                                <SlidersHorizontal className="h-4 w-4" />
                                <span>Filter</span>
                                {isAnyFilterActive && (
                                    <span className="flex h-2 w-2 rounded-full bg-emerald-500"></span>
                                )}
                            </button>
                            {isAnyFilterActive && (
                                <button
                                    onClick={resetFilters}
                                    title="Reset all filters"
                                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors shadow-sm focus:outline-none flex items-center justify-center w-5 h-5"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        {/* Refresh icon */}
                        <button className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
                            <RefreshCw className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="flex justify-center items-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50/60">
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Student</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Grade & Roll No</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Father</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Admission Date</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Badge & Campus</th>
                                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {paginatedStudents.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-5 py-12 text-center text-gray-400 text-sm">
                                            <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                            No students found
                                        </td>
                                    </tr>
                                ) : paginatedStudents.map((student: Student) => (
                                    <tr 
                                        key={student.id} 
                                        className="hover:bg-gray-50/70 transition-colors cursor-pointer select-none"
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            setContextMenu({
                                                x: e.clientX,
                                                y: e.clientY,
                                                student: student
                                            });
                                        }}
                                    >
                                        {/* Student */}
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <Avatar student={student} />
                                                <div className="min-w-0">
                                                    <div className="text-gray-500 font-mono text-[10px] uppercase font-bold">{student.studentId}</div>
                                                    <button
                                                        onClick={() => setViewingStudent(student)}
                                                        className="font-semibold text-gray-900 text-sm hover:text-emerald-600 transition-colors text-left"
                                                    >
                                                        {student.firstName} {student.lastName}
                                                    </button>
                                                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                                                        <span className="truncate max-w-[140px]">{student.email}</span>
                                                        <button
                                                            onClick={() => setViewingStudent(student)}
                                                            className="text-gray-400 hover:text-emerald-500 transition-colors"
                                                        >
                                                            <Eye className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        {/* Grade & Roll No */}
                                        <td className="px-5 py-3.5 text-gray-700">
                                            <div>Grade {student.academicInfo.currentGrade || '—'}</div>
                                            <div className="text-xs text-gray-400">Roll: {student.academicInfo.rollNumber || '—'}</div>
                                        </td>
                                        {/* Father */}
                                        <td className="px-5 py-3.5 text-gray-600 text-xs">{student.parentInfo?.fatherName || '—'}</td>
                                        {/* Admission Date */}
                                        <td className="px-5 py-3.5 text-gray-600 text-xs whitespace-nowrap">
                                            {student.academicInfo.admissionDate ? new Date(student.academicInfo.admissionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                        </td>
                                        <td className="px-5 py-3.5 text-xs text-gray-600">
                                            {(student.academicInfo as any)?.badge || (student as any).badge ? (
                                                <div>
                                                    <span className="font-semibold text-gray-800">
                                                        {typeof (student.academicInfo as any)?.badge === 'string' ? (student.academicInfo as any).badge : ((student as any).badge?.title || (student as any).badge)}
                                                    </span>
                                                    <div className="text-gray-400">{getStudentCampusName(student)}</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <span className="text-gray-400">—</span>
                                                    <div className="text-gray-400">{getStudentCampusName(student)}</div>
                                                </div>
                                            )}
                                        </td>
                                        {/* Status toggle */}
                                        <td className="px-5 py-3.5">
                                            <StatusToggle student={student} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination footer */}
                <div className="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-gray-50/40">
                    <span className="text-xs text-gray-500">
                        {filteredStudents.length === 0 ? '0' : `${startIndex + 1}-${Math.min(startIndex + itemsPerPage, filteredStudents.length)}`} of {filteredStudents.length} items
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>

                        {getPaginationRange().map((page, idx) => {
                            if (page === '...') {
                                return (
                                    <span key={idx} className="px-1 text-gray-400 text-sm font-semibold select-none">
                                        ...
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={idx}
                                    onClick={() => handlePageChange(page as number)}
                                    className={`w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${
                                        currentPage === page
                                            ? 'bg-red-500 text-white shadow-sm'
                                            : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        <select
                            value={itemsPerPage}
                            onChange={e => handleItemsPerPageChange(e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
                        >
                            <option value={10}>10 / page</option>
                            <option value={25}>25 / page</option>
                            <option value={50}>50 / page</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* ── Status change modal ──────────────────────────────────── */}
            {showStatusModal && selectedStudent && (
                <Modal
                    isOpen={true}
                    onClose={() => { setShowStatusModal(false); setSelectedStudent(null); setStatusReason(''); }}
                    title="Change Student Status"
                    size="sm"
                    footer={
                        <>
                            <Button type="button" variant="secondary" size="sm" onClick={() => { setShowStatusModal(false); setSelectedStudent(null); setStatusReason(''); }}>Cancel</Button>
                            <Button type="button" size="sm" onClick={confirmStatusChange} disabled={!statusReason.trim()}>Confirm Change</Button>
                        </>
                    }
                >
                    <div className="space-y-4">
                        <div className="text-center p-4 bg-amber-50/70 border border-amber-100 rounded-xl">
                            <h4 className="text-sm font-semibold text-amber-900 mb-1">Warning: Modifying Student Status</h4>
                            <p className="text-xs text-amber-700">Current: <span className="font-semibold capitalize">{selectedStudent.academicInfo.status}</span></p>
                            <p className="text-xs text-amber-700 mt-0.5">Target: <span className="font-bold capitalize">{selectedStudent.academicInfo.status === 'active' ? 'Inactive' : 'Active'}</span></p>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-2">Reason <span className="text-red-500">*</span></label>
                            <textarea
                                value={statusReason}
                                onChange={e => setStatusReason(e.target.value)}
                                placeholder="Please type the reason for updating this student's status..."
                                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                                rows={3}
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* ── Student view modal ───────────────────────────────────── */}
            {viewingStudent && (
                <Modal
                    isOpen={true}
                    onClose={() => setViewingStudent(null)}
                    title="Student Information"
                    size="lg"
                    footer={
                        <>
                            <Button type="button" variant="secondary" size="sm" onClick={() => setViewingStudent(null)}>Close</Button>
                            <Button type="button" size="sm" onClick={() => { setViewingStudent(null); onEdit(viewingStudent); }} className="flex items-center space-x-2">
                                <Edit className="h-4 w-4" /><span>Edit Student</span>
                            </Button>
                        </>
                    }
                >
                    <div className="space-y-6">
                        {/* Basic info */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <User className="h-4 w-4 mr-2 text-emerald-600" />Basic Information
                            </h4>
                            <div className="flex justify-center mb-5">
                                <div className="w-20 h-20 rounded-full overflow-hidden cursor-pointer ring-4 ring-emerald-50 shadow-md"
                                    onClick={() => viewingStudent.profileImage && handleImageClick(viewingStudent.profileImage, `${viewingStudent.firstName} ${viewingStudent.lastName}`)}>
                                    {viewingStudent.profileImage ? (
                                        <img src={viewingStudent.profileImage} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-emerald-500 flex items-center justify-center text-white text-2xl font-bold">
                                            {`${viewingStudent.firstName[0]}${viewingStudent.lastName?.[0] ?? ''}`.toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                {[
                                    ['Student ID', viewingStudent.studentId],
                                    ['First Name', viewingStudent.firstName],
                                    ['Last Name', viewingStudent.lastName || 'N/A'],
                                    ['Email', viewingStudent.email],
                                    ['Phone', viewingStudent.phone || 'N/A'],
                                    ['Date of Birth', viewingStudent.dateOfBirth ? new Date(viewingStudent.dateOfBirth).toLocaleDateString() : 'N/A'],
                                    ['Gender', viewingStudent.gender],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium capitalize truncate">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Academic */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <BookOpen className="h-4 w-4 mr-2 text-emerald-600" />Academic Information
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                {[
                                    ['Grade', viewingStudent.academicInfo.currentGrade],
                                    ['Section', viewingStudent.academicInfo.section || 'N/A'],
                                    ['Roll Number', viewingStudent.academicInfo.rollNumber || 'N/A'],
                                    ['Admission Date', viewingStudent.academicInfo.admissionDate ? new Date(viewingStudent.academicInfo.admissionDate).toLocaleDateString() : 'N/A'],
                                    ['Status', viewingStudent.academicInfo.status],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium capitalize">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Parent */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">Parent Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                {[
                                    ["Father's Name",  viewingStudent.parentInfo.fatherName  || 'N/A'],
                                    ["Mother's Name",  viewingStudent.parentInfo.motherName  || 'N/A'],
                                    ["Father's Phone", viewingStudent.parentInfo.fatherPhone || 'N/A'],
                                    ["Father's Email", viewingStudent.parentInfo.fatherEmail || 'N/A'],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Address */}
                        <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center border-b pb-1.5">
                                <MapPin className="h-4 w-4 mr-2 text-emerald-600" />Address
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                <div className="md:col-span-3">
                                    <span className="text-gray-500 block mb-1">Street</span>
                                    <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">{viewingStudent.address?.street || 'N/A'}</div>
                                </div>
                                {[
                                    ['City', viewingStudent.address?.city || 'N/A'],
                                    ['State', viewingStudent.address?.state || 'N/A'],
                                    ['Country', viewingStudent.address?.country || 'N/A'],
                                ].map(([label, val]) => (
                                    <div key={label}>
                                        <span className="text-gray-500 block mb-1">{label}</span>
                                        <div className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-gray-800 font-medium">{val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* ── Professional Filter Modal ────────────────────────────── */}
            {showFilterModal && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowFilterModal(false)}
                    title="Filter Students"
                    size="md"
                    footer={
                        <div className="flex items-center justify-between w-full">
                            <Button 
                                type="button" 
                                variant="secondary" 
                                size="md" 
                                onClick={resetFilters}
                                disabled={!isAnyFilterActive}
                            >
                                Reset All
                            </Button>
                            <div className="flex gap-2">
                                <Button 
                                    type="button" 
                                    variant="secondary" 
                                    size="md" 
                                    onClick={() => setShowFilterModal(false)}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="button" 
                                    size="md" 
                                    onClick={() => setShowFilterModal(false)}
                                >
                                    Apply Filters
                                </Button>
                            </div>
                        </div>
                    }
                >
                    <div className="space-y-4 text-xs text-left">
                        {/* Class Filter */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Class / Grade</label>
                            <select
                                value={selectedClass}
                                onChange={e => setSelectedClass(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-700 font-semibold"
                            >
                                <option value="all">All Classes</option>
                                {dbClasses.map(c => {
                                    const campus = campuses.find(cam => cam.id === c.campusId);
                                    const campusName = campus ? ` (${campus.name})` : '';
                                    return (
                                        <option key={c.id} value={`${c.grade}|${c.section}`}>
                                            Grade {c.grade} - Section {c.section}{campusName}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {/* Badge Filter */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Badge</label>
                            <select
                                value={selectedBadge}
                                onChange={e => setSelectedBadge(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-700 font-semibold"
                            >
                                <option value="all">All Badges</option>
                                {badges.map(b => (
                                    <option key={b.id} value={b.title}>
                                        {b.title} ({b.year})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Status</label>
                            <select
                                value={localFilterStatus}
                                onChange={e => setLocalFilterStatus(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-700 font-semibold"
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="graduated">Graduated</option>
                            </select>
                        </div>

                        {/* Profile Image Filter */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Has Profile Photo</label>
                            <div className="grid grid-cols-3 gap-2">
                                {(['all', 'yes', 'no'] as const).map(option => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => setFilterHasImage(option)}
                                        className={`py-2 px-3 border rounded-xl font-semibold capitalize text-center transition-all ${
                                            filterHasImage === option
                                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500'
                                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {option === 'all' ? 'All' : option === 'yes' ? 'Yes' : 'No'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Admission Date Range */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                                <Calendar className="h-3.5 w-3.5 text-gray-500" />
                                Admission Date Range
                            </label>
                            <DatePicker.RangePicker
                                getPopupContainer={() => document.body}
                                popupStyle={{ zIndex: 100000 }}
                                value={
                                    filterAdmissionFrom && filterAdmissionTo
                                        ? [dayjs(filterAdmissionFrom), dayjs(filterAdmissionTo)]
                                        : null
                                }
                                onChange={(dates) => {
                                    if (dates && dates[0] && dates[1]) {
                                        setFilterAdmissionFrom(dates[0].format('YYYY-MM-DD'));
                                        setFilterAdmissionTo(dates[1].format('YYYY-MM-DD'));
                                    } else {
                                        setFilterAdmissionFrom('');
                                        setFilterAdmissionTo('');
                                    }
                                }}
                                className="w-full h-10 rounded-xl border border-gray-300 focus-within:ring-2 focus-within:ring-emerald-500 text-xs font-medium hover:border-emerald-400"
                                style={{ borderRadius: '0.75rem' }}
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* Image preview */}
            <ImagePreview src={selectedImageUrl} alt={selectedImageAlt} visible={showImageModal} onVisibleChange={setShowImageModal} />

            {/* Context menu for row right click */}
            {contextMenu && (
                <div
                    className="fixed bg-white border border-gray-200 rounded-xl shadow-xl py-1.5 z-50 text-xs font-semibold text-gray-700 min-w-[120px] divide-y divide-gray-50 overflow-hidden"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                >
                    <button
                        onClick={() => {
                            setViewingStudent(contextMenu.student);
                            setContextMenu(null);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 transition-colors text-gray-700"
                    >
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                        <span>View Details</span>
                    </button>
                    <button
                        onClick={() => {
                            onEdit(contextMenu.student);
                            setContextMenu(null);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2 transition-colors text-emerald-600 hover:text-emerald-700"
                    >
                        <Edit className="w-3.5 h-3.5 text-emerald-500" />
                        <span>Edit Student</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default StudentView;
